import React, { useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import { usePageCacheState } from '../contexts/PageCacheContext';
import ErrorMessage from '../components/ErrorMessage';
import SuccessMessage from '../components/SuccessMessage';

export default function EmailAccounts() {
  const [showAddForm, setShowAddForm] = usePageCacheState<boolean>(
    'emailAccounts.showAddForm',
    false
  );
  const [loading, setLoading] = usePageCacheState<boolean>(
    'emailAccounts.loading',
    false
  );
  const [error, setError] = usePageCacheState<string>('emailAccounts.error', '');
  const [success, setSuccess] = usePageCacheState<string>(
    'emailAccounts.success',
    ''
  );
  const [emailAccounts, setEmailAccounts] = usePageCacheState<any[]>(
    'emailAccounts.list',
    []
  );
  const [loadingAccounts, setLoadingAccounts] = usePageCacheState<boolean>(
    'emailAccounts.loadingAccounts',
    true
  );
  const [formData, setFormData] = usePageCacheState('emailAccounts.formData', {
    email_address: '',
    provider: 'gmail',
    password: '',
    imap_host: '',
    imap_port: 993,
    use_ssl: true,
  });

  const providers = [
    { value: 'gmail', label: 'Gmail', host: 'imap.gmail.com', port: 993 },
    { value: 'outlook', label: 'Outlook/Hotmail', host: 'outlook.office365.com', port: 993 },
    { value: 'yahoo', label: 'Yahoo', host: 'imap.mail.yahoo.com', port: 993 },
    { value: 'custom', label: 'Özel/Diğer', host: '', port: 993 },
  ];

  const loadEmailAccounts = useCallback(async () => {
    try {
      setLoadingAccounts(true); // Keep this for initial loading state
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('No auth token found, cannot load email accounts.');
        return;
      }

      const response = await apiClient.get('/email-accounts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setEmailAccounts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading email accounts:', error);
      // Email hesapları yüklenirken hata oluştu
    } finally {
      setLoadingAccounts(false);
    }
  }, [setEmailAccounts, setLoadingAccounts]);

  // Load email accounts on component mount
  useEffect(() => {
    loadEmailAccounts();
  }, [loadEmailAccounts]);

  const deleteEmailAccount = async (accountId: string) => {
    if (!window.confirm('Bu email hesabını silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await apiClient.delete(`/email-accounts/${accountId}`);
      if (response.data.success) {
        setSuccess('Email hesabı başarıyla silindi!');
        await loadEmailAccounts();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error?.message || 'Email hesabı silinemedi');
      }
    } catch (error: any) {
      setError(error.response?.data?.error?.message || 'Email hesabı silinirken bir hata oluştu');
    }
  };

  const toggleEmailAccountStatus = async (accountId: string) => {
    try {
      const response = await apiClient.put(`/email-accounts/${accountId}/toggle`);
      if (response.data.success) {
        setSuccess(response.data.message);
        await loadEmailAccounts();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error?.message || 'Email hesabı durumu güncellenemedi');
      }
    } catch (error: any) {
      setError(error.response?.data?.error?.message || 'Email hesabı durumu güncellenirken bir hata oluştu');
    }
  };

  const handleProviderChange = (provider: string) => {
    const providerConfig = providers.find(p => p.value === provider);
    if (providerConfig) {
      setFormData(prev => ({
        ...prev,
        provider,
        imap_host: providerConfig.host,
        imap_port: providerConfig.port
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiClient.post('/email-accounts', {
        email_address: formData.email_address,
        provider: formData.provider,
        password: formData.password,
        imap_host: formData.imap_host,
        imap_port: formData.imap_port,
        use_ssl: formData.use_ssl
      });

      if (response.data.success) {
        setSuccess('Email hesabı başarıyla eklendi!');
        setFormData({
          email_address: '',
          provider: 'gmail',
          password: '',
          imap_host: '',
          imap_port: 993,
          use_ssl: true
        });
        // Reload email accounts list
        await loadEmailAccounts();
        setTimeout(() => {
          setShowAddForm(false);
          setSuccess('');
        }, 2000);
      } else {
        setError(response.data.error?.message || 'Bir hata oluştu');
      }
    } catch (error: any) {
      setError(error.response?.data?.error?.message || 'Email hesabı eklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {success && (
        <SuccessMessage
          message={success}
          onDismiss={() => setSuccess('')}
          autoHide={true}
        />
      )}

      {error && (
        <ErrorMessage
          message={error}
          onDismiss={() => setError('')}
          onRetry={() => loadEmailAccounts()}
        />
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Email Hesapları</h1>
          <p className="mt-2 text-gray-600">
            IMAP/POP3 email hesaplarınızı yönetin ve email taramalarını başlatın.
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="btn-primary"
        >
          + Email Hesabı Ekle
        </button>
      </div>

      {/* Add Email Account Form */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Email Hesabı Ekle</h3>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setError('');
                    setSuccess('');
                    setFormData({
                      email_address: '',
                      provider: 'gmail',
                      password: '',
                      imap_host: '',
                      imap_port: 993,
                      use_ssl: true
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <ErrorMessage
                    message={error}
                    onDismiss={() => setError('')}
                  />
                )}

                {success && (
                  <SuccessMessage
                    message={success}
                    onDismiss={() => setSuccess('')}
                    autoHide={true}
                  />
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Adresi</label>
                  <input
                    type="email"
                    required
                    value={formData.email_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_address: e.target.value }))}
                    className="input-field"
                    placeholder="ornek@gmail.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Sağlayıcı</label>
                  <select
                    value={formData.provider}
                    onChange={(e) => handleProviderChange(e.target.value)}
                    className="input-field"
                  >
                    {providers.map(provider => (
                      <option key={provider.value} value={provider.value}>
                        {provider.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Uygulama Şifresi
                    <span className="text-xs text-gray-500 ml-1">(Normal şifreniz değil)</span>
                  </label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="input-field"
                    placeholder="••••••••••••••••"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Gmail için: Google Hesabı → Güvenlik → Uygulama şifreleri
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">IMAP Sunucusu</label>
                    <input
                      type="text"
                      required
                      value={formData.imap_host}
                      onChange={(e) => setFormData(prev => ({ ...prev, imap_host: e.target.value }))}
                      className="input-field"
                      placeholder="imap.gmail.com"
                      disabled={formData.provider !== 'custom'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Port</label>
                    <input
                      type="number"
                      required
                      value={formData.imap_port}
                      onChange={(e) => setFormData(prev => ({ ...prev, imap_port: parseInt(e.target.value) }))}
                      className="input-field"
                      placeholder="993"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.use_ssl}
                      onChange={(e) => setFormData(prev => ({ ...prev, use_ssl: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">SSL/TLS Kullan (Önerilen)</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setError('');
                      setSuccess('');
                      setFormData({
                        email_address: '',
                        provider: 'gmail',
                        password: '',
                        imap_host: '',
                        imap_port: 993,
                        use_ssl: true
                      });
                    }}
                    disabled={loading}
                    className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Ekleniyor...
                      </div>
                    ) : (
                      'Hesabı Ekle'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Email Accounts List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Kayıtlı Email Hesapları</h3>
        </div>
        <div className="px-6 py-8">
          {loadingAccounts ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="border rounded-lg p-4 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-48"></div>
                        <div className="h-3 bg-gray-200 rounded w-32"></div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <div className="w-16 h-6 bg-gray-200 rounded"></div>
                      <div className="w-8 h-8 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : !emailAccounts || emailAccounts.length === 0 ? (
            <div className="text-center text-gray-500">
              <div className="text-4xl mb-4">📧</div>
              <p className="text-lg font-medium">Henüz email hesabı eklenmemiş</p>
              <p className="text-sm mt-2">
                Email hesabınızı ekleyerek email taramalarını başlatabilirsiniz.
              </p>
              <button
                onClick={() => setShowAddForm(true)}
                className="mt-4 btn-primary"
              >
                İlk Email Hesabınızı Ekleyin
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {emailAccounts && emailAccounts.map((account: any) => (
                <div key={account.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-lg">📧</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{account.email_address}</h4>
                        <p className="text-sm text-gray-500 capitalize">{account.provider}</p>
                        <p className="text-xs text-gray-400">
                          {account.imap_host}:{account.imap_port} {account.use_ssl ? '(SSL)' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleEmailAccountStatus(account.id)}
                        className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${account.is_active
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                      >
                        {account.is_active ? 'Aktif' : 'Pasif'}
                      </button>
                      <button
                        onClick={() => deleteEmailAccount(account.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                      >
                        Sil
                      </button>
                    </div>
                  </div>
                  {account.last_sync && (
                    <div className="mt-2 text-xs text-gray-400">
                      Son senkronizasyon: {new Date(account.last_sync).toLocaleString('tr-TR')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-4">💡 Email Hesabı Ekleme Rehberi</h3>
        <div className="space-y-4 text-sm text-blue-800">
          <div>
            <h4 className="font-medium">Gmail için:</h4>
            <ol className="list-decimal list-inside mt-1 space-y-1">
              <li>Google Hesabı → Güvenlik → 2 Adımlı Doğrulama'yı aktif edin</li>
              <li>Güvenlik → Uygulama şifreleri'ne gidin</li>
              <li>"Mail" kategorisinde yeni bir uygulama şifresi oluşturun</li>
              <li>Oluşturulan şifreyi buraya girin (normal Gmail şifrenizi değil)</li>
            </ol>
          </div>
          <div>
            <h4 className="font-medium">Outlook/Hotmail için:</h4>
            <ol className="list-decimal list-inside mt-1 space-y-1">
              <li>Microsoft Hesabı → Güvenlik → Gelişmiş güvenlik seçenekleri</li>
              <li>Uygulama şifreleri → Yeni uygulama şifresi oluştur</li>
              <li>Oluşturulan şifreyi buraya girin</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
